import { ProductDetailsDialog } from "@/components/customers/EventDetailsDialog";
import { CreateQuoteDialog } from "@/components/quotes/CreateQuoteDialog";
import {
  DealResubmissionCard,
  PhoneValidationCard,
} from "@/components/review/SubmissionReviewCards";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  formatCurrency,
  formatEventType,
  formatMiles,
  formatPaymentMethod,
  formatRate,
  getBuyOrSellBadge,
  getEventTypeBadgeVariant,
  getEventTypeIcon,
  getMiles,
  getOrderType,
  getOrderTypeIcon,
  getPaymentMethod,
  getProductName,
  getRate,
  getStageBadge,
  getTotalPayout,
} from "@/components/utils/deal-dialog-utils";
import { Deal } from "@/core/entities/deal";
import { Event } from "@/core/entities/event";
import { useGetCustomerById } from "@/hooks";
import { useProductsStore } from "@/stores/products";
import { transformDealValueToName } from "@/utils";
import { formatPrice } from "@/utils/pricing-formatter";
import FingerprintDisplay from "./FingerprintDisplay";
import { StageDurationPills } from "./StageDurationPills";
import TrackingDisplay from "./TrackingDisplay";

import { format, formatDistanceToNow } from "date-fns";
import {
  Clock,
  CreditCard,
  DollarSign,
  ExternalLink,
  Eye,
  FileText,
  Hash,
  Loader2,
  Mail,
  Package,
  Plane,
  Send,
  Settings2,
  Shield,
  Tag,
  User,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
// import { AuditLogDialog } from "@/components/audit-log/AuditLogDialog";

// Define available columns
const COLUMNS = {
  type: { key: "type", label: "Event Type" },
  product: { key: "product", label: "Product" },
  orderType: { key: "orderType", label: "Order Type" },
  miles: { key: "miles", label: "Miles" },
  rate: { key: "rate", label: "Rate" },
  totalPayout: { key: "totalPayout", label: "Total Payout" },
  paymentMethod: { key: "paymentMethod", label: "Payment Method" },
  created: { key: "created", label: "Created At" },
  actions: { key: "actions", label: "Actions" },
} as const;

interface DealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: Deal | null;
  events: Event[];
  isLoadingEvent: boolean;
}

export function DealDialog({
  open,
  onOpenChange,
  deal,
  events,
  isLoadingEvent,
}: Readonly<DealDialogProps>) {
  const { products } = useProductsStore();
  const customerQuery = useGetCustomerById(deal?.customerId ?? "");
  console.log(events);
  // Column visibility state - all columns visible by default
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    {
      type: true,
      product: true,
      orderType: true,
      miles: true,
      rate: false,
      totalPayout: false,
      paymentMethod: false,
      created: true,
      actions: true,
    },
  );

  // Event details dialog state
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);
  const [isResubmissionHistoryOpen, setIsResubmissionHistoryOpen] =
    useState(false);
  const [isPhoneHistoryOpen, setIsPhoneHistoryOpen] = useState(false);
  const [isRequoteOpen, setIsRequoteOpen] = useState(false);
  const shouldStretchReviewCards =
    isResubmissionHistoryOpen !== isPhoneHistoryOpen;

  // Audit log dialog state
  // const [auditLogOpen, setAuditLogOpen] = useState(false);

  const toggleColumn = (columnKey: string) => {
    setVisibleColumns((prev: Record<string, boolean>) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  console.log(deal);

  if (!deal) return null;
  const operationsCreateHref =
    deal.buyOrSell?.toLowerCase() === "sell"
      ? `/operations/purchase-orders?createMode=deal&sourceDealId=${deal.id}`
      : `/operations/sales-orders?createMode=deal&sourceDealId=${deal.id}`;
  const operationsCreateLabel =
    deal.buyOrSell?.toLowerCase() === "sell"
      ? "Create Purchase Order"
      : "Create Sell Order";
  const dealSide = deal.buyOrSell?.toLowerCase();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-4xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
      >
        <SheetHeader className="p-0">
          <SheetTitle>Deal Details</SheetTitle>
          <SheetDescription>
            Comprehensive view of the deal information and associated events
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6">
          <div className="space-y-4">
            {/* Deal Information */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Deal Information</h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!customerQuery.data}
                    onClick={() => setIsRequoteOpen(true)}
                  >
                    Re-quote
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to={operationsCreateHref}>
                      {operationsCreateLabel}
                    </Link>
                  </Button>
                </div>
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAuditLogOpen(true)}
                >
                  <History className="h-4 w-4 mr-2" />
                  View Audit Log
                </Button> */}
              </div>

              <div className="border rounded-lg p-6">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <h4 className="text-lg font-semibold">{deal.name}</h4>
                      <div className="flex items-center gap-2">
                        {getBuyOrSellBadge(deal.buyOrSell)}
                        {getStageBadge(
                          transformDealValueToName(deal.stage.toString()),
                        )}
                      </div>

                      {/* Stage Duration Pills */}
                      {deal.stageHistory && (
                        <div className="mt-3">
                          <div className="flex items-center gap-2 text-sm font-medium mb-2">
                            <Clock className="h-4 w-4" />
                            Deal stage tracker
                          </div>
                          <StageDurationPills deal={deal} />
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(deal.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(deal.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Deal Details Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Package className="h-4 w-4" />
                        Product Name
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.productName}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <DollarSign className="h-4 w-4" />
                        Amount
                      </div>
                      <p className="text-sm text-muted-foreground font-semibold">
                        ${deal.amount.toLocaleString()}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Hash className="h-4 w-4" />
                        Quantity
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.quantity.toLocaleString()}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Shield className="h-4 w-4" />
                        Status Tier
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.statusTier?.name} • {deal.statusTier?.percentage}%
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Tag className="h-4 w-4" />
                        Unit Price
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatPrice({
                          price: deal.unitPrice,
                          fractionDigits: 5,
                        })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <User className="h-4 w-4" />
                        Customer ID
                      </div>
                      <p className="text-sm text-muted-foreground font-mono">
                        {deal.customerId}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Clock className="h-4 w-4" />
                        Last Updated
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(deal.updatedAt).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Mail className="h-4 w-4" />
                        Account Email
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.accountEmail || "-"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <CreditCard className="h-4 w-4" />
                        Payment Method
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.paymentMethod || "-"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Mail className="h-4 w-4" />
                        PayPal Email
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.paypalEmail || "-"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <FileText className="h-4 w-4" />
                        Business Lost Reason
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.businessLostReason || "-"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <FileText className="h-4 w-4" />
                        Entered Pending
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {deal.enteredPending ? "Yes" : "No"}
                      </p>
                    </div>
                    {deal.lastSubmissionAt ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Clock className="h-4 w-4" />
                          Last Submission
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {format(
                            new Date(deal.lastSubmissionAt),
                            "MMM d, yyyy h:mm a",
                          )}
                        </p>
                      </div>
                    ) : null}
                    {deal.resubmissionCount !== undefined ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Send className="h-4 w-4" />
                          Resubmissions
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {deal.resubmissionCount}
                        </p>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <ExternalLink className="h-4 w-4" />
                        Hubspot Link
                      </div>
                      <Button>
                        <Send />
                        <Link
                          to={`https://app.hubspot.com/contacts/43829082/record/0-3/${deal.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Deal
                        </Link>
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <ExternalLink className="h-4 w-4" />
                        Customer Portal Link
                      </div>
                      <Button>
                        <Plane className="h-4 w-4" />
                        <Link
                          to={`https://portal.themilesmarket.com/secure?quote=${deal.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Portal
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-semibold">
                        Submission Review
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        Compact duplicate submission signals for the deal and
                        the linked customer&apos;s latest phone validation.
                      </p>
                    </div>
                    <div
                      className={`grid gap-4 xl:grid-cols-2 ${
                        shouldStretchReviewCards
                          ? "items-stretch"
                          : "items-start"
                      }`}
                    >
                      <DealResubmissionCard
                        deal={deal}
                        onHistoryOpenChange={setIsResubmissionHistoryOpen}
                      />
                      <PhoneValidationCard
                        result={customerQuery.data?.latestPhoneValidation}
                        history={customerQuery.data?.phoneValidationHistory}
                        title="Customer Phone Validation"
                        onHistoryOpenChange={setIsPhoneHistoryOpen}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Marketing Attribution */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Marketing Attribution</h3>
              </div>
              <TrackingDisplay tracking={deal.tracking} />
            </div>

            {/* Device Fingerprints */}
            {deal.fingerprints && deal.fingerprints.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Device Fingerprints ({deal.fingerprints.length})
                  </h3>
                </div>
                <FingerprintDisplay data={deal.fingerprints} />
              </div>
            )}

            {/* Associated Events */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Associated Events ({events?.length || 0})
                </h3>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Settings2 className="h-4 w-4 mr-2" />
                      Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {Object.values(COLUMNS).map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.key}
                        className="capitalize"
                        checked={visibleColumns[column.key]}
                        onCheckedChange={() => toggleColumn(column.key)}
                      >
                        {column.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {isLoadingEvent ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading events...
                </div>
              ) : events && events.length > 0 ? (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {visibleColumns.type && (
                          <TableHead>Event Type</TableHead>
                        )}
                        {visibleColumns.product && (
                          <TableHead>Product</TableHead>
                        )}
                        {visibleColumns.orderType && (
                          <TableHead>Order Type</TableHead>
                        )}
                        {visibleColumns.miles && <TableHead>Miles</TableHead>}
                        {visibleColumns.rate && <TableHead>Rate</TableHead>}
                        {visibleColumns.totalPayout && (
                          <TableHead>Total Payout</TableHead>
                        )}
                        {visibleColumns.paymentMethod && (
                          <TableHead>Payment Method</TableHead>
                        )}
                        {visibleColumns.created && (
                          <TableHead>Created At</TableHead>
                        )}
                        {visibleColumns.actions && (
                          <TableHead>Actions</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((event, index) => {
                        const productName = getProductName(
                          event.productId,
                          products,
                        );
                        const orderType = getOrderType(event);
                        const miles = getMiles(event);
                        const rate = getRate(event);
                        const totalPayout = getTotalPayout(event);
                        const paymentMethod = getPaymentMethod(event);

                        return (
                          <TableRow key={event.id || index}>
                            {visibleColumns.type && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {getEventTypeIcon(event.type)}
                                  <Badge
                                    variant={
                                      getEventTypeBadgeVariant(event.type) as
                                        | "default"
                                        | "secondary"
                                        | "destructive"
                                        | "outline"
                                    }
                                  >
                                    {formatEventType(event.type)}
                                  </Badge>
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.product && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-muted-foreground" />
                                  {productName || "-"}
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.orderType && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {getOrderTypeIcon(orderType)}
                                  {orderType || "-"}
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.miles && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Hash className="h-4 w-4 text-muted-foreground" />
                                  {formatMiles(miles)}
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.rate && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                                  {formatRate(rate)}
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.totalPayout && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                                  {formatCurrency(totalPayout)}
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.paymentMethod && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                                  {formatPaymentMethod(paymentMethod)}
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.created && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  {format(
                                    new Date(event.createdAt),
                                    "MMM dd, yyyy HH:mm:ss",
                                  )}
                                </div>
                              </TableCell>
                            )}
                            {visibleColumns.actions && (
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedEvent(event);
                                    setIsEventDetailsOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center p-8 text-muted-foreground border rounded-lg">
                  No events found for this deal.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>

      <ProductDetailsDialog
        open={isEventDetailsOpen}
        onOpenChange={setIsEventDetailsOpen}
        product={
          selectedEvent
            ? products.find((p) => p.id === selectedEvent.productId) || null
            : null
        }
        event={selectedEvent}
      />

      {customerQuery.data && (
        <CreateQuoteDialog
          open={isRequoteOpen}
          onOpenChange={setIsRequoteOpen}
          customer={customerQuery.data}
          defaults={{
            productId: deal.productId,
            buyOrSell:
              dealSide === "buy" || dealSide === "sell" ? dealSide : undefined,
            quantity: deal.quantity,
            statusTier: deal.statusTier?.name,
          }}
        />
      )}

      {/* <AuditLogDialog
        open={auditLogOpen}
        onOpenChange={setAuditLogOpen}
        documentId={deal?.id || ""}
        documentName={deal?.name}
      /> */}
    </Sheet>
  );
}
